import { useState } from 'react';
import { Twitter, Linkedin, Link2, Facebook, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface FloatingShareButtonsProps {
  url: string;
  title: string;
}

export const FloatingShareButtons = ({ url, title }: FloatingShareButtonsProps) => {
  const [copied, setCopied] = useState(false);

  const shareOnTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  const shareOnLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const buttons = [
    { icon: Twitter, onClick: shareOnTwitter, label: 'Twitter' },
    { icon: Linkedin, onClick: shareOnLinkedIn, label: 'LinkedIn' },
    { icon: Facebook, onClick: shareOnFacebook, label: 'Facebook' },
    { icon: copied ? Check : Link2, onClick: copyLink, label: 'Copy Link' },
  ];

  const ButtonContent = () => (
    <>
      {buttons.map((button, index) => (
        <motion.button
          key={button.label}
          onClick={button.onClick}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className={cn(
            "p-3 rounded-xl bg-black/40 backdrop-blur-md border border-white/10",
            "hover:border-[#00C2FF]/40 hover:shadow-[0_0_15px_rgba(0,194,255,0.2)]",
            "transition-all duration-300"
          )}
          aria-label={button.label}
        >
          <button.icon className="w-5 h-5 text-white/70 hover:text-white" />
        </motion.button>
      ))}
    </>
  );

  return (
    <>
      {/* Desktop: Fixed Left Side */}
      <div className="hidden lg:flex fixed left-6 top-1/2 -translate-y-1/2 z-40 flex-col gap-3">
        <ButtonContent />
      </div>

      {/* Mobile: Sticky Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-black/60 backdrop-blur-md border-t border-white/10 p-3 safe-area-pb">
        <div className="flex justify-center gap-4">
          <ButtonContent />
        </div>
      </div>
    </>
  );
};
